/**
 * The voice-transcription WebSocket (WS /api/v1/transcription/stream). OpenAPI
 * can't describe WebSockets, so this one is hand-written; it's the only place
 * web/ opens it.
 *
 * - Browsers can't set headers on a WebSocket, so the token rides the
 *   subprotocols as ["bearer", token], keeping it out of URLs and logs.
 * - Up: binary PCM16 mono 24 kHz frames, then {"type":"stop"} to finish.
 * - Down: partial / final / error, validated here; anything else is reported
 *   and skipped.
 */

import { z } from "zod";
import { getRuntimeConfig } from "@/config/runtime";
import { reportError } from "@/telemetry/telemetry";
import { getAccessToken } from "./tokens";

/** Close codes the backend uses (backend/src/api/routes/transcription.py). */
export const TRANSCRIPTION_CLOSE = { notConfigured: 4503, internalError: 4500 } as const;

const zTranscriptionMessage = z.discriminatedUnion("type", [
  z.object({ type: z.literal("partial"), text: z.string() }),
  z.object({ type: z.literal("final"), text: z.string() }),
  z.object({ type: z.literal("error"), message: z.string() }),
]);

export type TranscriptionMessage = z.infer<typeof zTranscriptionMessage>;

export interface TranscriptionHandlers {
  onMessage: (message: TranscriptionMessage) => void;
  /** The server closed the socket after it opened (never after `close()`). */
  onClose: (code: number) => void;
}

export interface TranscriptionSocket {
  sendAudio: (chunk: ArrayBuffer) => void;
  /** Ask the server to transcribe what it has and finish. */
  stop: () => void;
  /** Close now, with no further callbacks. */
  close: () => void;
}

/** The socket closed before it opened, or there was no token to open it with. */
export class TranscriptionConnectError extends Error {
  constructor(readonly code: number) {
    super(`The transcription socket didn't open (code ${code})`);
    this.name = "TranscriptionConnectError";
  }
}

function parseMessage(data: unknown): TranscriptionMessage | null {
  let json: unknown = null;
  try {
    json = typeof data === "string" ? JSON.parse(data) : null;
  } catch {
    // Reported below with every other frame that doesn't match the contract.
  }
  const parsed = zTranscriptionMessage.safeParse(json);
  if (parsed.success) return parsed.data;
  reportError(new Error("Malformed transcription message"), { source: "transcription" });
  return null;
}

/** Open the socket; resolves once it's open. */
export async function openTranscriptionSocket(handlers: TranscriptionHandlers): Promise<TranscriptionSocket> {
  const token = await getAccessToken();
  if (!token) throw new TranscriptionConnectError(1008);
  const socket = new WebSocket(`${getRuntimeConfig().wsApiUrl}/transcription/stream`, ["bearer", token]);
  socket.binaryType = "arraybuffer";
  const send = (data: ArrayBuffer | string) => {
    if (socket.readyState === WebSocket.OPEN) socket.send(data);
  };
  let opened = false;
  let closedByUs = false;
  return new Promise((resolve, reject) => {
    socket.onmessage = (event) => {
      const message = parseMessage(event.data);
      if (message) handlers.onMessage(message);
    };
    socket.onclose = (event) => {
      if (!opened) reject(new TranscriptionConnectError(event.code));
      else if (!closedByUs) handlers.onClose(event.code);
    };
    socket.onopen = () => {
      opened = true;
      resolve({
        sendAudio: send,
        stop: () => send(JSON.stringify({ type: "stop" })),
        close: () => {
          closedByUs = true;
          socket.close();
        },
      });
    };
  });
}
